import { useState } from 'react';
import { D, st, toArr, apiGet, apiPost, API_URL, getToken, apiErrorMessage } from '../constants';
import { SendDocModal, DocView } from './extraComponents.jsx';
import { InvoiceBadge } from './shared';

export default function Invoices({invoices, clients, clientName, openInvoiceModal, apiUpdateInvoice, apiDeleteInvoice, fmt, fmtDate, calcTotal, companyInfo, payments, apiSavePayment, apiDeletePayment, me, expenses=[]}) {
  const [detail, setDetail] = useState(null);
  const [sendModal, setSendModal] = useState(null);
  const [pmtForm, setPmtForm] = useState({amount:'', date:new Date().toISOString().slice(0,10), method:'Check', notes:''});
  const [pmtSaving, setPmtSaving] = useState(false);
  const [reminderSending, setReminderSending] = useState(false);
  const [reminderMsg, setReminderMsg] = useState('');

  // Receipt image viewer. The backend mints a 15-minute signed R2 URL on
  // demand; this endpoint has existed since the image feature shipped and
  // until now nothing in the app called it - the photos were reachable only
  // from the command line.
  const [receiptView, setReceiptView] = useState(null);
  // A blob: URL is held by the browser until revoked, so closing must free it.
  const closeReceipt = () => setReceiptView(v => { if (v && v.url) { try { URL.revokeObjectURL(v.url); } catch (e) {} } return null; });
  const viewReceipt = async (rid, label) => {
    setReceiptView({ rid, label, loading: true });
    try {
      // Fetched with the bearer token and wrapped in a blob: URL, because an
      // <img> cannot carry an Authorization header. Nothing bearer-capable
      // reaches the page and a copied link is useless to anyone not logged in.
      const res = await fetch(API_URL.replace("/api","") + "/api/data/receipts/" + rid + "/file", { headers: { Authorization: "Bearer " + getToken() } });
      if (!res.ok) { setReceiptView({ rid, label, error: await apiErrorMessage(res) }); return; }
      setReceiptView({ rid, label, url: URL.createObjectURL(await res.blob()) });
    } catch (err) {
      setReceiptView({ rid, label, error: (err && err.message) || 'Could not load the receipt image.' });
    }
  };

  const isOwner = me?.user?.role === 'owner';

  // Cost attached to an invoice comes from TWO places, and counting only the
  // first is what made INV-001 read 89% margin when it was really 72%:
  //   (a) expenses pointed straight at this invoice (linked_invoice), and
  //   (b) expenses pointed at the JOB this invoice bills for (linked_job_id).
  // Billing a material and attaching its cost were independent acts - pick the
  // job as a receipt line's destination, which is the natural choice when there
  // IS a job, and the cost vanished from the invoice's P/L while the line was
  // still billed. invoices.job_id has existed all along and nothing read it.
  // Rows arrive snake_case from the API; camelCase fallbacks are for a future
  // mapper. String() both sides - linked_invoice is a TEXT column.
  const invJobId = inv => {
    const j = inv?.jobId ?? inv?.job_id ?? null;
    return j === null || j === '' ? null : Number(j);
  };
  const invRows = inv => {
    const direct = toArr(expenses).filter(e =>
      String(e.linked_invoice ?? e.linkedInvoice ?? '') === String(inv.id));
    const jid = invJobId(inv);
    // A job cost already claimed by SOME invoice belongs to that invoice, not
    // this one - that is what keeps a job spanning several invoices from having
    // its costs counted more than once.
    const viaJob = jid === null ? [] : toArr(expenses).filter(e =>
      Number(e.linked_job_id ?? e.linkedJobId ?? 0) === jid &&
      !String(e.linked_invoice ?? e.linkedInvoice ?? ''));
    // NOT filtered on billable: a cost you absorbed is still a cost. That is
    // the same rule the flat-rate work settled - billable=0 means "never a line
    // item", never "free".
    const seen = new Set();
    const out = [];
    for (const e of [...direct, ...viaJob]) {
      if (seen.has(e.id)) continue;
      seen.add(e.id);
      out.push({ ...e, _viaJob: !direct.includes(e) });
    }
    return out.sort((a,b)=>String(a.date||'').localeCompare(String(b.date||'')) || ((a.id||0)-(b.id||0)));
  };
  const invCost = inv => invRows(inv).reduce((s,e)=>s+(Number(e.amount)||0),0);
  // Does this invoice bill for a job without being linked to one? Then its
  // job's costs cannot be found and the margin shown is too high.
  const invJobGroups = inv => {
    let its = inv?.items;
    if (typeof its === 'string') { try { its = JSON.parse(its); } catch (e) { its = []; } }
    return toArr(its).filter(i => i && i.type === 'job').length;
  };

  if (detail) {
    const inv = detail;
    const ce = clients.find(c => c.id === inv.clientId)?.email || "";
    const cp = clients.find(c => c.id === inv.clientId)?.phone || "";
    const invPmts = toArr(payments).filter(p => p.invoice_id === inv.id);
    const totalPaid = invPmts.reduce((s, p) => s + (p.amount || 0), 0);
    const invTotal = calcTotal(inv.items);
    const balance = invTotal - totalPaid;

    const recordPayment = async () => {
      const amt = Number(pmtForm.amount);
      if (!amt) return;
      setPmtSaving(true);
      try {
        await apiSavePayment({invoice_id:inv.id, client_id:inv.clientId, amount:amt, date:pmtForm.date, method:pmtForm.method, notes:pmtForm.notes});
        setPmtForm(f => ({...f, amount:'', notes:''}));
        if (balance - amt <= 0 && inv.status !== 'Paid') {
          await apiUpdateInvoice(inv.id, {status:'Paid', paidDate:pmtForm.date});
          setDetail(d => ({...d, status:'Paid', paidDate:pmtForm.date}));
        }
      } catch(e) {}
      setPmtSaving(false);
    };

    return (
      <div>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20,flexWrap:"wrap"}}>
          <button style={st.btn()} onClick={()=>setDetail(null)}>Back</button>
          <h1 style={{...st.h1,margin:0}}>{inv.number}</h1>
          <InvoiceBadge status={inv.status}/>
          <button style={st.btn("primary")} onClick={()=>openInvoiceModal(inv)}>Edit</button>
          <button style={st.btn()} onClick={()=>setSendModal(inv)}>Send</button>
          {isOwner && inv.status!=="Paid" && <button style={st.btn()} disabled={reminderSending} onClick={async()=>{
            setReminderSending(true); setReminderMsg('');
            try {
              const r = await apiPost(`/email/reminder/${inv.id}`, {});
              setReminderMsg('Reminder sent' + (r.provider ? ` (${r.provider})` : ''));
            } catch(e) {
              let msg = e.message || 'Failed to send reminder';
              try { msg = JSON.parse(msg).error || msg; } catch {}
              setReminderMsg('Error: ' + msg);
            }
            setReminderSending(false);
            setTimeout(()=>setReminderMsg(''), 5000);
          }}>{reminderSending ? 'Sending...' : 'Send Reminder'}</button>}
          {reminderMsg && <span style={{fontSize:12,color:reminderMsg.startsWith('Error')?D.danger:D.success}}>{reminderMsg}</span>}
          {inv.status!=="Paid" && <button style={st.btn("success")} onClick={async()=>{
            const paidDate=new Date().toISOString().slice(0,10);
            await apiUpdateInvoice(inv.id, {status:"Paid", paidDate});
            setDetail({...inv, status:"Paid", paidDate});
          }}>Mark Paid</button>}
          <button style={st.btn("danger")} onClick={()=>{
            if(confirm(`Delete invoice ${inv.number}?`)){ apiDeleteInvoice(inv.id); setDetail(null); }
          }}>Delete</button>
        </div>

        <div style={{...st.card, marginBottom:16}}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16}}>
            <div><div style={{fontSize:11,color:D.muted,marginBottom:4}}>Invoice total</div><div style={{fontSize:18,fontWeight:500}}>{fmt(invTotal)}</div></div>
            <div><div style={{fontSize:11,color:D.muted,marginBottom:4}}>Paid</div><div style={{fontSize:18,fontWeight:500,color:totalPaid>0?D.success:D.muted}}>{fmt(totalPaid)}</div></div>
            <div><div style={{fontSize:11,color:D.muted,marginBottom:4}}>Balance due</div><div style={{fontSize:18,fontWeight:600,color:balance>0?D.danger:D.success}}>{fmt(balance)}</div></div>
            <div><div style={{fontSize:11,color:D.muted,marginBottom:4}}>Due date</div><div style={{fontSize:18,fontWeight:500}}>{fmtDate(inv.dueDate)}</div></div>
          </div>
        </div>

        {isOwner && (()=>{
          const costRows = invRows(inv);
          const cost = costRows.reduce((s,e)=>s+(Number(e.amount)||0),0);
          const profit = invTotal - cost;
          return (
            <div style={{...st.card, marginBottom:16}}>
              <div style={{fontWeight:600, marginBottom:12}}>Profit / Loss</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16,marginBottom:14}}>
                <div><div style={{fontSize:11,color:D.muted,marginBottom:4}}>Billed</div><div style={{fontSize:18,fontWeight:500}}>{fmt(invTotal)}</div></div>
                <div><div style={{fontSize:11,color:D.muted,marginBottom:4}}>Cost attached</div><div style={{fontSize:18,fontWeight:500,color:cost>0?D.warning:D.muted}}>{fmt(cost)}</div></div>
                <div><div style={{fontSize:11,color:D.muted,marginBottom:4}}>Gross profit</div><div style={{fontSize:18,fontWeight:600,color:profit>=0?D.success:D.danger}}>{fmt(profit)}{invTotal>0?" ("+(profit/invTotal*100).toFixed(0)+"%)":""}</div></div>
              </div>
              {costRows.length>0 ? (
                <div style={{overflowX:"auto"}}><table style={st.table}>
                  <thead><tr>{['Date','Item','Category','Vendor','Cost','Source','Receipt'].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
                  <tbody>{costRows.map(e=>(
                    <tr key={e.id}>
                      <td style={st.td}>{fmtDate(e.date)}</td>
                      <td style={st.td}>{String(e.description||'').replace(/&amp;/g,'&')}</td>
                      <td style={st.td}>{e.category||'-'}</td>
                      <td style={st.td}>{e.vendor||'-'}</td>
                      <td style={st.td}>{fmt(e.amount)}</td>
                      <td style={st.td}>{e._viaJob
                        ? <span style={{fontSize:11,color:D.muted}} title="Recorded against the job this invoice bills for, not against the invoice itself.">via job</span>
                        : <span style={{fontSize:11,color:D.muted}}>invoice</span>}</td>
                      <td style={st.td}>{(e.receipt_id ?? e.receiptId)
                        ? <button style={{...st.btn(),marginLeft:0,fontSize:11,padding:'2px 8px'}}
                            onClick={()=>viewReceipt(e.receipt_id ?? e.receiptId, e.vendor||'')}>View</button>
                        : <span style={{color:D.muted,fontSize:11}} title="Logged before receipt capture - no photo was stored.">-</span>}</td>
                    </tr>
                  ))}</tbody>
                </table></div>
              ) : (
                <div style={{color:D.muted,fontSize:13}}>No costs attached. Scan a receipt and set a line's destination to this invoice.</div>
              )}
              {invJobGroups(inv) > 0 && invJobId(inv) === null && (
                <div style={{marginTop:12,padding:'8px 10px',borderRadius:6,border:'1px solid '+D.warning,color:D.warning,fontSize:12,lineHeight:1.5}}>
                  This invoice bills for a job, but no job is linked to it. Any materials you bought
                  against that job are not counted below, so the margin shown is too high.
                  Open Edit and pick the job to include them.
                </div>
              )}
              <div style={{fontSize:11,color:D.muted,marginTop:10}}>
                Your own labor is not a cost here - it is your pay. Employee wages are not included yet.
                {invJobId(inv) !== null && <> Costs recorded against the linked job are included and marked <em>via job</em>.</>}
              </div>

              {receiptView && (
                <div onClick={()=>closeReceipt()}
                  style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
                  <div onClick={ev=>ev.stopPropagation()}
                    style={{background:D.surface,borderRadius:10,padding:14,maxWidth:'min(900px,95vw)',maxHeight:'92vh',display:'flex',flexDirection:'column',gap:10}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:12,flexWrap:'wrap'}}>
                      <div style={{fontWeight:600}}>Receipt #{receiptView.rid}{receiptView.label?' - '+receiptView.label:''}</div>
                      <div style={{display:'flex',gap:8}}>
                        {/* noreferrer matters here: a signed R2 URL in a Referer
                            header would hand the credential to whatever loads next. */}
                        {receiptView.url && <a href={receiptView.url} target="_blank" rel="noreferrer"
                          style={{...st.btn(),marginLeft:0,textDecoration:'none'}}>Open full size</a>}
                        <button style={{...st.btn(),marginLeft:0}} onClick={()=>viewReceipt(receiptView.rid, receiptView.label)}>Reload</button>
                        <button style={{...st.btn('danger'),marginLeft:0}} onClick={()=>closeReceipt()}>Close</button>
                      </div>
                    </div>
                    {receiptView.loading && <div style={{color:D.muted,fontSize:13,padding:'40px 10px',textAlign:'center'}}>Loading the photo...</div>}
                    {receiptView.error && <div style={{color:D.danger,fontSize:13,padding:'24px 10px'}}>{receiptView.error}</div>}
                    {receiptView.url && <img src={receiptView.url} alt={'Receipt '+receiptView.rid}
                      onError={()=>setReceiptView(v=>v&&({...v,url:null,error:'Could not load the image from storage. Press Reload to try again.'}))}
                      style={{maxWidth:'100%',maxHeight:'76vh',objectFit:'contain',borderRadius:6,background:'#000'}}/>}
                    <div style={{fontSize:11,color:D.muted}}>Served through AmpBooks, not a public link. Press Reload if it fails.</div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        <div style={{...st.card, marginBottom:16}}>
          <div style={{fontWeight:600, marginBottom:12}}>Payments</div>
          {invPmts.length > 0 && (
            <div style={{overflowX:"auto"}}><table style={{...st.table, marginBottom:14}}>
              <thead><tr>{['Date','Amount','Method','Notes',''].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
              <tbody>
                {invPmts.map(p=>(
                  <tr key={p.id}>
                    <td style={st.td}>{fmtDate(p.date)}</td>
                    <td style={st.td}>{fmt(p.amount)}</td>
                    <td style={st.td}>{p.method||'-'}</td>
                    <td style={st.td}>{p.notes||'-'}</td>
                    <td style={st.td}>
                      {isOwner && <button onClick={()=>{if(confirm('Delete payment?'))apiDeletePayment(p.id);}} style={{...st.btn('danger'),fontSize:11,padding:'2px 8px'}}>Delete</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
          {invPmts.length===0 && <div style={{color:D.muted,fontSize:13,marginBottom:14}}>No payments recorded yet.</div>}
          {balance > 0 && (
            <div style={{display:'flex',gap:8,alignItems:'flex-end',flexWrap:'wrap'}}>
              <div>
                <div style={{fontSize:11,color:D.muted,marginBottom:3}}>Amount</div>
                <input type="number" min="0" step="0.01" placeholder="0.00" style={{...st.input,width:100}} value={pmtForm.amount} onChange={e=>setPmtForm(f=>({...f,amount:e.target.value}))}/>
              </div>
              <div>
                <div style={{fontSize:11,color:D.muted,marginBottom:3}}>Date</div>
                <input type="date" style={{...st.input,width:140}} value={pmtForm.date} onChange={e=>setPmtForm(f=>({...f,date:e.target.value}))}/>
              </div>
              <div>
                <div style={{fontSize:11,color:D.muted,marginBottom:3}}>Method</div>
                <select style={{...st.input,width:110}} value={pmtForm.method} onChange={e=>setPmtForm(f=>({...f,method:e.target.value}))}>
                  {['Cash','Check','Card','ACH','Other'].map(m=><option key={m}>{m}</option>)}
                </select>
              </div>
              <div style={{flex:1,minWidth:120}}>
                <div style={{fontSize:11,color:D.muted,marginBottom:3}}>Notes</div>
                <input style={{...st.input,width:'100%'}} placeholder="optional" value={pmtForm.notes} onChange={e=>setPmtForm(f=>({...f,notes:e.target.value}))}/>
              </div>
              <button onClick={recordPayment} disabled={pmtSaving||!pmtForm.amount} style={{...st.btn('success'),opacity:(pmtSaving||!pmtForm.amount)?0.6:1}}>
                {pmtSaving?'Saving...':'Record Payment'}
              </button>
            </div>
          )}
        </div>

        <DocView doc={inv} clientName={clientName(inv.clientId)} fmtDate={fmtDate} type="Invoice" extra={{dueDate:inv.dueDate,paidDate:inv.paidDate}} companyInfo={companyInfo}/>
        {sendModal && <SendDocModal doc={sendModal} type="Invoice" clientEmail={ce} clientPhone={cp} onClose={()=>setSendModal(null)} onMarkSent={async()=>{
          await apiUpdateInvoice(inv.id, {status:"Sent"});
          setDetail({...inv, status:"Sent"});
          setSendModal(null);
        }} companyInfo={companyInfo}/>}
      </div>
    );
  }

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:8}}>
        <div style={st.h1}>Invoices</div>
        <button style={st.btn("primary")} onClick={()=>openInvoiceModal(null)}>+ New Invoice</button>
      </div>
      <div style={st.card}>
        <div style={{overflowX:"auto"}}><table style={st.table}>
          <thead><tr>{["#","Client","Date","Due","Total",...(isOwner?["Profit"]:[]),"Status"].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
          <tbody>{toArr(invoices).map(inv=>(
            <tr key={inv.id} style={{cursor:"pointer"}} onClick={()=>setDetail(inv)}>
              <td style={st.td}><span style={{color:D.accent}}>{inv.number}</span></td>
              <td style={st.td}>{clientName(inv.clientId)}</td>
              <td style={st.td}>{fmtDate(inv.date)}</td>
              <td style={st.td}>{fmtDate(inv.dueDate)}</td>
              <td style={st.td}>{fmt(calcTotal(inv.items))}</td>
              {isOwner && <td style={st.td}>{(()=>{
                const c=invCost(inv); if(!c) return <span style={{color:D.muted}}>-</span>;
                const p=calcTotal(inv.items)-c;
                return <span style={{color:p>=0?D.success:D.danger,fontWeight:500}}>{fmt(p)}</span>;
              })()}</td>}
              <td style={st.td}><InvoiceBadge status={inv.status}/>{inv.status!=='Paid'&&inv.status!=='Cancelled'&&inv.dueDate&&new Date(inv.dueDate)<new Date()&&<span style={{marginLeft:6,fontSize:10,fontWeight:700,color:'#fff',background:'#dc2626',borderRadius:4,padding:'2px 5px',verticalAlign:'middle',letterSpacing:'0.03em'}}>PAST DUE</span>}</td>
            </tr>
          ))}</tbody>
        </table></div>
        {toArr(invoices).length===0 && <div style={{padding:"30px 0",textAlign:"center",color:D.muted,fontSize:13}}>No invoices yet.</div>}
      </div>
    </div>
  );
}
